import React from "react";
import { SearchOutlined } from "@mui/icons-material";
import { Box, styled, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "@fontsource/plus-jakarta-sans";
import { useNavigate } from "react-router-dom";
import type { Size } from "../../../types";

const sizes: Record<Size, { h: string; w: string }> = {
    small: { h: `310px`, w: `100%` },
    medium: { h: `250px`, w: `100%` },
    large: { h: `250px`, w: `100%` },
};

interface ResponsiveSettings {
    fontSize?: number;
    fWeight?: number;
    leftPadding?: string;
    searchWidth?: string;
    topMargin?: number;
    lineHeight?: number;
    searchMargin?: string;
    gradient?: string;
    align?: React.CSSProperties["textAlign"];
}

const setSizes = (width: number, setResponsive: React.Dispatch<React.SetStateAction<ResponsiveSettings>>): void => {
    if (width < 475) {
        return setResponsive({
            fontSize: 28,
            fWeight: 600,
            leftPadding: `30px`,
            searchWidth: `100%`,
            topMargin: 0,
            lineHeight: 1.6,
            searchMargin: `20px`,
            gradient: `top`,
            align: `center`,
        });
    }
    if (width < 790) {
        return setResponsive({
            fontSize: 30,
            leftPadding: `25px`,
            topMargin: 0,
            searchWidth: `45%`,
            lineHeight: 1.5,
            fWeight: 700,
            searchMargin: `30px`,
            gradient: `right`,
            align: `left`,
        });
    }

    setResponsive({
        fontSize: 30,
        leftPadding: `60px`,
        topMargin: 0,
        searchWidth: `45%`,
        lineHeight: 1.5,
        fWeight: 700,
        searchMargin: `30px`,
        gradient: `right`,
        align: `left`,
    });
};

interface WrapProps {
    size: Size;
    leftPadding?: string;
    gradient?: string;
    viewportWidth: number;
}

const Wrap = styled(Box)<WrapProps>(({ size, leftPadding, gradient, viewportWidth }) => ({
    fontFamily: `Plus Jakarta Sans`,
    maxWidth: sizes[size]?.w,
    maxHeight: sizes[size]?.h,
    padding: size === `small` ? 30 : `40px 60px`,
    paddingLeft: leftPadding,
    borderRadius: viewportWidth < 1050 ? 10 : `10px 0 0 10px`,
    width: `100%`,
    minHeight: 200,
    position: `relative`,
    cursor: `pointer`,
    background:
        gradient === `right`
            ? `linear-gradient(to right, rgb(47, 42, 43) 0%, rgb(47, 42, 43) 40%, transparent 100%), 
    url("https://li-mall-images.s3.us-east-1.amazonaws.com/hostedassets/cbr-hero-image.png") right top / contain no-repeat;`
            : `linear-gradient(0deg, #231f20bf, #000000bf) repeat 0 0 / auto,  
            url("https://li-mall-images.s3.us-east-1.amazonaws.com/hostedassets/cbr-hero-image.png") no-repeat 100% / cover;`,
}));

interface TextBoxProps extends ResponsiveSettings {
    size?: Size;
}

const TextBox = styled(Box)<TextBoxProps>(({ fontSize, lineHeight, align, fWeight }) => ({
    fontSize,
    color: `white`,
    fontWeight: fWeight,
    textAlign: align,
    lineHeight,
    fontFamily: `inherit`,
}));

let debounce: ReturnType<typeof setTimeout> | null = null;

interface CustomBigCarouselProps {
    size: Size;
    viewportWidth: number;
}

const CustomBigCarousel = ({ size, viewportWidth }: CustomBigCarouselProps): React.ReactElement => {
    const [search, setSearch] = useState<string>(``);
    const [responsive, setResponsive] = useState<ResponsiveSettings>({});
    const navigate = useNavigate();
    const doSearch = (v: string): void => {
        if (v) navigate(`/search?q=${encodeURIComponent(v)}`);
    };
    const ref = useRef<HTMLDivElement>(null);

    const doResize = (): void => {
        if (debounce) return;
        debounce = setTimeout(() => {
            if (debounce) clearTimeout(debounce);
            debounce = null;
            const width = ref?.current?.clientWidth;
            if (width) {
                setSizes(width, setResponsive);
            }
        }, 25);
    };

    useEffect(() => {
        window.addEventListener(`resize`, doResize);
        window.requestAnimationFrame(() => doResize());
        return () => window.removeEventListener(`resize`, doResize);
    }, []);

    return (
        <Wrap {...responsive} ref={ref} size={size} viewportWidth={viewportWidth}>
            <TextBox sx={{ marginTop: responsive.topMargin }} size={size} {...responsive}>
                Search for Cash Back
            </TextBox>
            <TextBox size={size} {...responsive}>
                from Thousands of Merchants
            </TextBox>
            <TextBox size={size} {...responsive}>
                <Box sx={{ position: `relative` }}>
                    <TextField
                        value={search}
                        id="hero-search"
                        onKeyUp={e => {
                            if (e.key === `Enter`) {
                                doSearch(search);
                            }
                        }}
                        onChange={v => setSearch(v.target.value)}
                        InputProps={{
                            endAdornment: (
                                <Box
                                    onClick={() => doSearch(search)}
                                    sx={{
                                        position: `absolute`,
                                        cursor: `pointer`,
                                        right: `5px`,
                                        top: `4px`,
                                        background: `#01a652`,
                                        color: `white`,
                                        width: ` 32px`,
                                        height: `32px`,
                                        borderRadius: `3px`,
                                        padding: `2px`,
                                    }}
                                >
                                    <SearchOutlined sx={{ width: 28, height: 28 }} />
                                </Box>
                            ),
                        }}
                        sx={{
                            height: `40px`,
                            mt: responsive.searchMargin,
                            borderRadius: 1.25,
                            fontSize: `22px`,
                            position: `relative`,
                            border: `none`,
                            outline: `none`,

                            width: responsive.searchWidth,
                            minWidth: size === `small` ? 250 : 325,

                            "& .MuiInputBase-root": {
                                paddingRight: 0,
                            },
                            "& input": {
                                height: `40px`,
                                borderRadius: 1.25,
                                padding: `0 40px 0 20px`,
                                background: `#FFF`,
                                fontSize: `16px`,
                                color: `#333`,
                                border: `none`,
                                outline: `none`,
                                "&::placeholder": {
                                    color: `#333`,
                                },
                            },
                            "& fieldset": {
                                border: `none`,
                            },
                        }}
                        placeholder="Search"
                    />
                </Box>
            </TextBox>
        </Wrap>
    );
};

export default CustomBigCarousel;
